import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, Download, Loader2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { exportElementToA4Pdf } from '../../../utils/previewPdfExport';
import Dropdown from '../../ui/Dropdown/Dropdown';
import ExportProgressModal from '../ExportProgressModal/ExportProgressModal';
import styles from './PrintPreviewModal.module.css';

const STORAGE_KEY = 'printPreviewFormat';
const PRINT_MODES = ['receipt', 'a4'];

const PrintPreviewModal = ({
  title, onClose, onDownloadPdf, onPrint, printMode, onPrintModeChange, renderReceipt, renderA4, children, pdfFileName,
}) => {
  const { t } = useLocalization();
  const printAreaRef = useRef(null);
  const [downloadStatus, setDownloadStatus] = useState(null); // null | 'running' | 'done' | 'error'
  const [downloadProgress, setDownloadProgress] = useState(0);
  const isDownloading = downloadStatus === 'running';
  // Only show the receipt/A4 toggle when a caller actually offers both formats. A caller that
  // passes only one of the two (e.g. GRN's A4-only print) gets that one rendered directly, with
  // no dropdown and no risk of defaulting to the renderer it never provided.
  const hasToggle = !!(renderReceipt && renderA4);
  const [internalPrintMode, setInternalPrintMode] = useState(() => {
    if (hasToggle) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (PRINT_MODES.includes(saved)) return saved;
      } catch (e) { /* storage unavailable */ }
    }
    return renderReceipt ? 'receipt' : 'a4';
  });
  const hasAnyRenderer = !!(renderReceipt || renderA4);
  const resolvedMode = printMode || internalPrintMode;
  const handleModeChange = (mode) => {
    if (hasToggle) {
      try { localStorage.setItem(STORAGE_KEY, mode); } catch (e) { /* storage unavailable */ }
    }
    if (onPrintModeChange) onPrintModeChange(mode);
    else setInternalPrintMode(mode);
  };

  useEffect(() => {
    const isA4Mode = hasAnyRenderer && resolvedMode === 'a4';
    const styleId = 'print-preview-page-size';
    let pageSizeStyle = document.getElementById(styleId);

    if (!pageSizeStyle) {
      pageSizeStyle = document.createElement('style');
      pageSizeStyle.id = styleId;
      document.head.appendChild(pageSizeStyle);
    }

    document.body.classList.add('printing-preview');
    document.body.classList.toggle('printing-preview-a4', isA4Mode);
    document.body.classList.toggle('printing-preview-receipt', !isA4Mode);
    pageSizeStyle.textContent = isA4Mode
      ? '@media print { @page { size: A4 portrait !important; margin: 0 !important; } }'
      : '';

    return () => {
      document.body.classList.remove('printing-preview', 'printing-preview-a4', 'printing-preview-receipt');
      pageSizeStyle.remove();
    };
  }, [hasAnyRenderer, resolvedMode]);

  const handlePrint = () => (onPrint ? onPrint(resolvedMode) : window.print());

  // In A4 mode the download is a capture of the rendered preview, so it matches it exactly.
  const canCaptureA4 = !!renderA4 && resolvedMode === 'a4';
  const handleDownload = async () => {
    setDownloadProgress(0);
    setDownloadStatus('running');
    // Let the progress popup paint before the (blocking) capture starts
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      if (canCaptureA4) {
        const sheet = printAreaRef.current?.firstElementChild || printAreaRef.current;
        const fileName = pdfFileName || `${(title || 'document').replace(/[^\w\-#]+/g, '_')}.pdf`;
        try {
          await exportElementToA4Pdf(sheet, fileName, setDownloadProgress);
        } catch (err) {
          console.error('PDF capture failed:', err);
          await onDownloadPdf?.();
        }
      } else {
        await onDownloadPdf?.();
      }
      setDownloadStatus('done');
    } catch (err) {
      console.error('PDF download failed:', err);
      setDownloadStatus('error');
    }
  };

  const modalWidthClass = hasAnyRenderer ? (resolvedMode === 'receipt' ? styles.modalReceipt : styles.modalA4) : '';

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${modalWidthClass}`}>
        <div className={styles.modalHeader}>
          <h3>{title}</h3>

          <div className={styles.headerRight}>
            {hasToggle && (
              <Dropdown
                value={resolvedMode}
                onChange={(mode) => mode && handleModeChange(mode)}
                options={[
                  { value: 'receipt', label: t('offlineSales.receiptPrint', 'Print Receipt (80mm)') },
                  { value: 'a4', label: t('offlineSales.a4Print', 'Print A4 Sheet') },
                ]}
                placeholder={t('offlineSales.selectPrintFormat', 'Select Print Format')}
                allowClear={false}
                size="compact"
                className={styles.formatSelect}
              />
            )}
            <button onClick={onClose} className={styles.modalClose}><X size={18} /></button>
          </div>
        </div>

        <div
          className={`${styles.printArea} ${hasAnyRenderer && resolvedMode === 'a4' ? styles.a4Mode : ''}`}
          id="print-preview-content"
          ref={printAreaRef}
        >
          {hasAnyRenderer ? (resolvedMode === 'receipt' ? renderReceipt?.() : renderA4?.()) : children}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelBtn}>{t('common.cancel', 'Cancel')}</button>
          
          {(onDownloadPdf || canCaptureA4) && (
            <button
              onClick={handleDownload}
              className={styles.iconDownloadBtn}
              title={t('offlineSales.downloadA4Pdf', 'Download A4 PDF')}
              disabled={isDownloading}
            >
              {isDownloading ? <Loader2 size={18} className={styles.spinning} /> : <Download size={18} />}
            </button>
          )}

          <button onClick={handlePrint} className={styles.actionBtnSecondary}>
            <Printer size={16} /> {t('common.print', 'Print')}
          </button>
        </div>
      </div>

      <ExportProgressModal
        isOpen={!!downloadStatus}
        onClose={() => setDownloadStatus(null)}
        status={downloadStatus}
        totalRecords={canCaptureA4 ? 100 : 0}
        fetchedRecords={downloadProgress}
        title={t('common.downloadingPdf', 'Downloading PDF')}
        runningText={t('common.generatingPdf', 'Generating PDF…')}
        doneText={t('common.pdfDownloaded', 'Download complete! Your PDF has been saved.')}
        error={t('common.pdfDownloadFailed', 'Failed to generate the PDF. Please try again.')}
      />
    </div>,
    document.body
  );
};

export default PrintPreviewModal;
